import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";

export default function Clients() {
  const { t } = useT();
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const load = async (query = q, statusVal = statusFilter) => {
    try {
      const { data } = await api.get("/clients", {
        params: { q: query, status: statusVal === "all" ? "" : statusVal },
      });
      setClients(data);
    } catch (e) {
      setClients([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-slate-900" data-testid="clients-title">{t("clients.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("clients.subtitle")}</p>
        </div>
        {clients && <span className="text-sm text-slate-500" data-testid="clients-count">{t("clients.total", { n: clients.length })}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder={t("clients.search")}
          data-testid="clients-search"
          className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-800"
        />
        <div className="flex gap-2" data-testid="clients-status-filters">
          {["active", "inactive", "all"].map((f) => (
            <button key={f} data-testid={`clients-filter-${f}`}
              onClick={() => { setStatusFilter(f); load(q, f); }}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                statusFilter === f
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>
              {f === "all" ? t("recv.all") : t(`val.${f === "active" ? "Active" : "Inactive"}`)}
            </button>
          ))}
        </div>
      </div>

      {clients === null ? null : clients.length === 0 ? (
        <div className="text-sm text-slate-500 py-12 text-center bg-white border border-slate-200 rounded-lg" data-testid="clients-empty">
          {t("clients.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="clients-table">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2">{t("import.col_name")}</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2">{t("import.col_legacy_id")}</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2">{t("import.col_reg")}</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2">{t("import.col_vat")}</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2">{t("import.col_country")}</th>
                <th className="text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2">{t("clients.source")}</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td className="px-3 py-2 text-sm text-slate-800 border-t border-slate-100 font-medium">
                    <Link to={`/clients/${c.id}`} data-testid={`client-link-${c.id}`} className="hover:text-amber-700 hover:underline transition-colors">
                      {c.name}
                    </Link>
                    {c.status === "Inactive" && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-500 border-slate-200"
                        data-testid={`client-status-${c.id}`}>
                        {t("val.Inactive")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600 border-t border-slate-100">{c.legacy_id || "—"}</td>
                  <td className="px-3 py-2 text-sm text-slate-600 border-t border-slate-100">{c.registration_number || "—"}</td>
                  <td className="px-3 py-2 text-sm text-slate-600 border-t border-slate-100">{c.vat_number || "—"}</td>
                  <td className="px-3 py-2 text-sm text-slate-600 border-t border-slate-100">{c.country || "—"}</td>
                  <td className="px-3 py-2 text-sm text-slate-500 border-t border-slate-100">{c.source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
